import React, { useCallback, useEffect, useRef, useState } from 'react';

const clampVolume = (value) => {
    const normalizedValue = Number(value);

    if (!Number.isFinite(normalizedValue)) {
        return 1;
    }

    return Math.min(Math.max(normalizedValue, 0), 1);
};

const formatTime = (seconds) => {
    const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

export default React.memo(function BackgroundAudioPlayer({
    audioUrl,
    title = 'Nhạc nền',
    defaultVolume = 0.6,
    autoPauseOnUnmount = true,
    className = ''
}) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [volume, setVolume] = useState(clampVolume(defaultVolume));
    const [error, setError] = useState('');

    const resetAudioState = useCallback(() => {
        setIsPlaying(false);
        setDuration(0);
        setCurrentTime(0);
        setError('');
    }, []);

    useEffect(() => {
        setVolume(clampVolume(defaultVolume));
    }, [defaultVolume]);

    useEffect(() => {
        const audioElement = audioRef.current;

        if (!audioElement) {
            return undefined;
        }

        audioElement.volume = volume;
        return undefined;
    }, [volume]);

    useEffect(() => {
        const audioElement = audioRef.current;

        if (!audioElement) {
            return undefined;
        }

        const handleLoadedMetadata = () => {
            setDuration(audioElement.duration || 0);
            setError('');
        };

        const handleTimeUpdate = () => {
            setCurrentTime(audioElement.currentTime || 0);
        };

        const handlePlay = () => {
            setIsPlaying(true);
            setError('');
        };

        const handlePause = () => {
            setIsPlaying(false);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        const handleError = () => {
            setIsPlaying(false);
            setError('Không thể tải hoặc phát file âm thanh.');
        };

        audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
        audioElement.addEventListener('timeupdate', handleTimeUpdate);
        audioElement.addEventListener('play', handlePlay);
        audioElement.addEventListener('pause', handlePause);
        audioElement.addEventListener('ended', handleEnded);
        audioElement.addEventListener('error', handleError);

        return () => {
            audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audioElement.removeEventListener('timeupdate', handleTimeUpdate);
            audioElement.removeEventListener('play', handlePlay);
            audioElement.removeEventListener('pause', handlePause);
            audioElement.removeEventListener('ended', handleEnded);
            audioElement.removeEventListener('error', handleError);
        };
    }, [audioUrl]);

    useEffect(() => {
        const audioElement = audioRef.current;

        resetAudioState();

        if (!audioElement) {
            return undefined;
        }

        audioElement.pause();
        audioElement.load();

        return () => {
            if (autoPauseOnUnmount) {
                audioElement.pause();
                audioElement.currentTime = 0;
            }
        };
    }, [audioUrl, autoPauseOnUnmount, resetAudioState]);

    const handleTogglePlay = useCallback(async () => {
        const audioElement = audioRef.current;

        if (!audioElement || !audioUrl) {
            return;
        }

        try {
            if (audioElement.paused) {
                await audioElement.play();
            } else {
                audioElement.pause();
            }
        } catch (playError) {
            setError('Trình duyệt đã chặn phát tự động. Hãy bấm phát lại.');
        }
    }, [audioUrl]);

    const handleSeek = useCallback((event) => {
        const audioElement = audioRef.current;
        const nextTime = Number(event.target.value);

        if (!audioElement || !Number.isFinite(nextTime)) {
            return;
        }

        audioElement.currentTime = nextTime;
        setCurrentTime(nextTime);
    }, []);

    const handleVolumeChange = useCallback((event) => {
        setVolume(clampVolume(Number(event.target.value)));
    }, []);

    return (
        <div className={`alert alert-light border mb-0 p-1 ${className}`.trim()}>
            <audio ref={audioRef} preload="metadata">
                {audioUrl ? <source src={audioUrl} /> : null}
            </audio>

            <div className="d-flex flex-column gap-2">
                <div className="fw-semibold">
                    <i className="fas fa-music me-2"></i>
                    {title}
                </div>

                <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-3">
                    <div className="d-flex align-items-center gap-2 flex-grow-1 w-100">
                        <small className="text-muted flex-shrink-0">{formatTime(currentTime)}</small>
                        <input
                            type="range"
                            min="0"
                            max={Math.max(duration, 0)}
                            step="1"
                            value={Math.min(currentTime, duration || 0)}
                            onChange={handleSeek}
                            disabled={!audioUrl || duration <= 0}
                            className="form-range m-0"
                        />
                        <small className="text-muted flex-shrink-0">{formatTime(duration)}</small>
                    </div>

                    <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={handleTogglePlay}
                            disabled={!audioUrl}
                        >
                            <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'} me-1`}></i>
                            {isPlaying ? 'Dừng' : 'Phát'}
                        </button>
                        {/* <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleStop}
                            disabled={!audioUrl && currentTime === 0}
                        >
                            <i className="fas fa-stop me-1"></i>
                            Dừng
                        </button> */}
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-shrink-0" style={{ width: '140px', minWidth: '140px' }}>
                        <i className={`fas ${volume === 0 ? 'fa-volume-mute' : 'fa-volume-up'} text-muted`}></i>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={volume}
                            onChange={handleVolumeChange}
                            className="form-range m-0"
                            style={{ width: '60px' }}
                            disabled={!audioUrl}
                        />
                        <small className="text-muted flex-shrink-0">{Math.round(volume * 100)}%</small>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-warning py-2 mb-0" role="alert">
                        <small>{error}</small>
                    </div>
                )}
            </div>
        </div>
    );
});